/**
 * Company Location Map Component
 * Shows a Mapbox map with the company's location
 */

import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { getCompanyLogoDefaultSrc } from '../../../../../utils/company/get-company-logo-url';

// Dynamically import Mapbox to avoid SSR issues
type MapboxGL = typeof import('mapbox-gl').default;
let mapboxgl: MapboxGL | null = null;

interface CompanyLocationMapProps {
  coordinates: { lat: number; lng: number };
  companyName?: string;
  address?: string | null;
  companyId?: string;
  className?: string;
}

export const CompanyLocationMap: React.FC<CompanyLocationMapProps> = ({
  coordinates,
  companyName,
  address,
  companyId,
  className = '',
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapboxGL['Map'] | null>(null);
  const markerRef = useRef<MapboxGL['Marker'] | null>(null);
  const popupRef = useRef<MapboxGL['Popup'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const mapboxToken = import.meta.env.PUBLIC_ENV__MAPBOX_ACCESS_TOKEN;

  // Function to create headquarters-style marker
  const createHeadquartersMarker = (name?: string, addr?: string | null, compId?: string) => {
    // Create marker element with building icon style
    const markerEl = document.createElement('div');
    markerEl.className = 'company-headquarters-marker';
    markerEl.style.width = '40px';
    markerEl.style.height = '40px';
    markerEl.style.position = 'relative';
    markerEl.style.cursor = 'pointer';
    
    // Building icon SVG
    const buildingSvg = `
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="8" y="12" width="24" height="20" rx="2" fill="#3b82f6" stroke="white" stroke-width="2"/>
        <rect x="12" y="16" width="3" height="3" fill="white"/>
        <rect x="17" y="16" width="3" height="3" fill="white"/>
        <rect x="22" y="16" width="3" height="3" fill="white"/>
        <rect x="27" y="16" width="3" height="3" fill="white"/>
        <rect x="12" y="22" width="3" height="3" fill="white"/>
        <rect x="17" y="22" width="3" height="3" fill="white"/>
        <rect x="22" y="22" width="3" height="3" fill="white"/>
        <rect x="27" y="22" width="3" height="3" fill="white"/>
        <path d="M20 8L24 12H16L20 8Z" fill="#3b82f6" stroke="white" stroke-width="1.5"/>
      </svg>
    `;
    markerEl.innerHTML = buildingSvg;
    markerEl.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
    
    // Create iOS 16 style popup content
    const popupContent = document.createElement('div');
    popupContent.style.padding = '0';
    popupContent.style.minWidth = '240px';
    popupContent.style.maxWidth = '280px';
    popupContent.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    popupContent.style.background = 'rgba(255, 255, 255, 0.95)';
    popupContent.style.backdropFilter = 'blur(20px) saturate(180%)';
    popupContent.style.borderRadius = '16px';
    popupContent.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)';
    popupContent.style.overflow = 'hidden';
    popupContent.style.border = '0.5px solid rgba(0, 0, 0, 0.08)';
    
    // Container for content
    const contentContainer = document.createElement('div');
    contentContainer.style.padding = '16px';
    contentContainer.style.display = 'flex';
    contentContainer.style.flexDirection = 'row';
    contentContainer.style.gap = '12px';
    contentContainer.style.alignItems = 'flex-start';
    
    // Logo container
    if (compId) {
      const logoContainer = document.createElement('div');
      logoContainer.style.flexShrink = '0';
      logoContainer.style.width = '48px';
      logoContainer.style.height = '48px';
      logoContainer.style.borderRadius = '10px';
      logoContainer.style.background = '#f9fafb';
      logoContainer.style.display = 'flex';
      logoContainer.style.alignItems = 'center';
      logoContainer.style.justifyContent = 'center';
      logoContainer.style.overflow = 'hidden';
      logoContainer.style.border = '0.5px solid rgba(0, 0, 0, 0.06)';
      
      const logoImg = document.createElement('img');
      const logoUrlToUse = getCompanyLogoDefaultSrc(compId);
      logoImg.src = logoUrlToUse;
      logoImg.alt = `${name || 'Company'} logo`;
      logoImg.style.width = '100%';
      logoImg.style.height = '100%';
      logoImg.style.objectFit = 'contain';
      logoImg.style.padding = '4px';
      logoImg.onerror = () => {
        logoImg.src = '/company-logo-default.svg';
      };
      
      logoContainer.appendChild(logoImg);
      contentContainer.appendChild(logoContainer);
    }
    
    // Text content container
    const textContainer = document.createElement('div');
    textContainer.style.flex = '1';
    textContainer.style.minWidth = '0';
    
    if (name || addr) {
      if (name) {
        const nameEl = document.createElement('div');
        nameEl.style.fontWeight = '600';
        nameEl.style.fontSize = '15px';
        nameEl.style.color = '#1d1d1f';
        nameEl.style.marginBottom = addr ? '4px' : '0';
        nameEl.style.lineHeight = '1.3';
        nameEl.style.letterSpacing = '-0.01em';
        nameEl.textContent = name;
        textContainer.appendChild(nameEl);
      }
      
      if (addr) {
        const addrEl = document.createElement('div');
        addrEl.style.fontSize = '13px';
        addrEl.style.color = '#6e6e73';
        addrEl.style.lineHeight = '1.4';
        addrEl.style.letterSpacing = '-0.01em';
        addrEl.textContent = addr;
        textContainer.appendChild(addrEl);
      }
    } else {
      const defaultEl = document.createElement('div');
      defaultEl.style.fontSize = '13px';
      defaultEl.style.color = '#6e6e73';
      defaultEl.textContent = 'Company Headquarters';
      textContainer.appendChild(defaultEl);
    }
    
    contentContainer.appendChild(textContainer);
    popupContent.appendChild(contentContainer);
    
    // Popup will be created when mapboxgl is available
    return { markerEl, popupContent };
  };

  useEffect(() => {
    if (!mapContainer.current || !mapboxToken || typeof window === 'undefined') {
      setIsLoading(false);
      if (!mapboxToken) {
        setError('Mapbox token not configured');
      }
      return;
    }

    if (map.current) {
      // Map already initialized, just update marker
      // Remove all existing popups from the map first
      const popups = map.current.getContainer().querySelectorAll('.mapboxgl-popup');
      popups.forEach((popup) => {
        const popupElement = popup as HTMLElement;
        if (popupElement.parentNode) {
          popupElement.parentNode.removeChild(popupElement);
        }
      });
      
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      
      const { markerEl, popupContent } = createHeadquartersMarker(companyName, address, companyId);
      
      markerRef.current = new mapboxgl!.Marker(markerEl)
        .setLngLat([coordinates.lng, coordinates.lat])
        .addTo(map.current);
      
      // Create and add popup directly to map (not attached to marker to avoid double popup)
      const popup = new mapboxgl!.Popup({ offset: 25, closeButton: false })
        .setLngLat([coordinates.lng, coordinates.lat])
        .setDOMContent(popupContent)
        .addTo(map.current);
      
      popupRef.current = popup;
      
      map.current.flyTo({
        center: [coordinates.lng, coordinates.lat],
        zoom: 7,
        duration: 1000,
      });
      
      return;
    }

    // Load Mapbox dynamically
    async function loadMapbox() {
      if (mapboxgl) {
        initializeMap();
        return;
      }

      try {
        const module = await import('mapbox-gl');
        mapboxgl = module.default;
        await import('mapbox-gl/dist/mapbox-gl.css');

        if (mapContainer.current && mapboxToken && !map.current) {
          initializeMap();
        }
      } catch (err) {
        console.error('[CompanyLocationMap] Error loading Mapbox:', err);
        setError('Failed to load map');
        setIsLoading(false);
      }
    }

    function initializeMap() {
      if (!mapContainer.current || !mapboxgl || map.current) {
        return;
      }

      try {
        mapboxgl.accessToken = mapboxToken;

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/light-v11',
          center: [coordinates.lng, coordinates.lat],
          zoom: 7,
        });

        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

        map.current.on('load', () => {
          setIsLoading(false);
          
          // Ensure no existing popups
          const popups = map.current!.getContainer().querySelectorAll('.mapboxgl-popup');
          popups.forEach((popup) => {
            const popupElement = popup as HTMLElement;
            if (popupElement.parentNode) {
              popupElement.parentNode.removeChild(popupElement);
            }
          });
          
          // Add headquarters marker
          const { markerEl, popupContent } = createHeadquartersMarker(companyName, address, companyId);
          
          markerRef.current = new mapboxgl.Marker(markerEl)
            .setLngLat([coordinates.lng, coordinates.lat])
            .addTo(map.current);
          
          // Create and add popup directly to map (not attached to marker to avoid double popup)
          const popup = new mapboxgl.Popup({ offset: 25, closeButton: false })
            .setLngLat([coordinates.lng, coordinates.lat])
            .setDOMContent(popupContent)
            .addTo(map.current);
          
          popupRef.current = popup;
        });

        map.current.on('error', () => {
          setError('Failed to load map');
          setIsLoading(false);
        });
      } catch (err) {
        console.error('[CompanyLocationMap] Error initializing map:', err);
        setError('Failed to initialize map');
        setIsLoading(false);
      }
    }

    loadMapbox();

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
      }
      if (markerRef.current) {
        markerRef.current.remove();
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [coordinates, companyName, address, companyId, mapboxToken]);

  if (error) {
    return (
      <div className={`bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-center ${className}`}>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <FormattedMessage id="jobDetailPage.mapError" defaultMessage="Unable to load map" description="Error message shown when company location map fails to load" />
        </p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg z-10">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <FormattedMessage id="jobDetailPage.loadingMap" defaultMessage="Loading map..." description="Loading message shown while company location map is initializing" />
          </div>
        </div>
      )}
      <div
        ref={mapContainer}
        className="w-full h-[400px] rounded-lg overflow-hidden"
        style={{ minHeight: '400px' }}
      />
    </div>
  );
};
